import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyWeeklyRecapPreview, sendMyWeeklyRecap } from "@/lib/weekly-recap.functions";
import { getMyWeeklyGoal } from "@/lib/goals.functions";
import { getMyReferralStatus } from "@/lib/referrals.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { WeeklyGoalTracker } from "@/components/WeeklyGoalTracker";
import { ShareRecapButton } from "@/components/ShareRecapButton";
import { toast } from "sonner";


export const Route = createFileRoute("/_authenticated/bartender/recap")({
  component: RecapPage,
  head: () => ({
    meta: [
      { title: "My weekly recap · The Hospitality Reserve" },
      { name: "description", content: "Preview your weekly bartender recap before it lands in your inbox." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => <div className="p-10 text-center">Could not load recap.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

function fmtGBP(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function RecapPage() {
  const fetchRecap = useServerFn(getMyWeeklyRecapPreview);
  const fetchGoal = useServerFn(getMyWeeklyGoal);
  const fetchReferral = useServerFn(getMyReferralStatus);
  const sendRecap = useServerFn(sendMyWeeklyRecap);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["weekly-recap-preview"],
    queryFn: () => fetchRecap(),
  });
  const { data: goalData } = useQuery({ queryKey: ["weekly-goal"], queryFn: () => fetchGoal() });
  const { data: referral } = useQuery({ queryKey: ["referral-status"], queryFn: () => fetchReferral() });

  async function handleSend() {
    setSending(true);
    try {
      const res = await sendRecap();
      setSentAt(res.sent_at);
      toast.success("Recap sent — check your inbox and notifications.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send recap.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <header className="space-y-3">
          <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground">Preview</p>
          <h1 className="font-display text-3xl">Your week in review</h1>
          {data && (
            <p className="text-sm text-muted-foreground">
              {fmtDate(data.window_start)} – {fmtDate(data.window_end)}. This is what your Monday recap email will say.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button onClick={handleSend} disabled={sending || isLoading}>
              {sending ? "Sending…" : "Send weekly recap"}
            </Button>
            {data && (
              <ShareRecapButton
                name="My week"
                earnings={fmtGBP(data.earnings_pence)}
                hours={`${data.hours.toFixed(data.hours % 1 ? 1 : 0)}h`}
                shifts={String(data.completed_shifts)}
                streak={String(data.streak ?? 0)}
                tagline="Staffing made easy."
                referralCode={referral?.code ?? null}
                badges={data.badges.map((b) => b.label)}
                nextShift={
                  data.next_shift
                    ? `${data.next_shift.role_type} · ${new Date(data.next_shift.start_time).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                    : null
                }
                projected={
                  goalData?.goal?.earnings_pence_target
                    ? `${fmtGBP(goalData.projection.earnings_pence)} projected · goal ${fmtGBP(goalData.goal.earnings_pence_target)}`
                    : null
                }
              />
            )}
            {sentAt && (
              <span className="text-xs text-muted-foreground">
                Last sent {new Date(sentAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </header>

        <WeeklyGoalTracker />


        {isLoading || !data ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 border border-border bg-muted/20 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Earnings" value={fmtGBP(data.earnings_pence)} />
              <Stat label="Hours" value={`${data.hours.toFixed(data.hours % 1 ? 1 : 0)}h`} />
              <Stat label="Streak" value={data.streak ? `${data.streak} wk` : "—"} />
              <Stat label="Completed" value={String(data.completed_shifts)} />
            </section>

            <section className="border border-border bg-card p-5">
              <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground mb-2">Next up</p>
              {data.next_shift ? (
                <p className="font-display text-lg">
                  {data.next_shift.role_type} ·{" "}
                  {new Date(data.next_shift.start_time).toLocaleString("en-GB", {
                    weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nothing booked yet — <Link to="/bartender" className="underline">browse the register</Link>.
                </p>
              )}
            </section>

            <section className="border border-border bg-card p-5">
              <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground mb-3">Badges this week</p>
              {data.badges.length ? (
                <ul className="flex flex-wrap gap-2">
                  {data.badges.map((b) => (
                    <li
                      key={`${b.label}-${b.unlocked_at}`}
                      className="text-[0.65rem] tracking-wide-luxury uppercase border border-accent/40 text-accent px-2 py-1"
                    >
                      {b.label}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No new milestones this week — keep stacking shifts.</p>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border bg-card p-4">
      <p className="text-[0.55rem] tracking-wide-luxury uppercase text-muted-foreground">{label}</p>
      <p className="font-display text-2xl mt-1 tabular-nums">{value}</p>
    </div>
  );
}
